import React, { useState, useMemo, useEffect } from 'react';
import toast from 'react-hot-toast';
import {
  Sparkles,
  ThumbsUp,
  Minus,
  ThumbsDown,
  Loader2,
  Calendar,
  X,
  ArrowLeft,
  Lightbulb,
  Brain,
  Target,
  Info,
  AlertTriangle,
  AlertOctagon,
  Zap,
} from 'lucide-react';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import { subDays, startOfMonth, parseISO, isWithinInterval } from 'date-fns';
import { useConversations } from '../../hooks/whatsapp/useChat';
import { useWaDashboardAI } from '../../hooks/whatsapp/useWaDashboardAI';
import { useWaLocalInsights } from '../../hooks/whatsapp/useWaLocalInsights';
import {
  useWaAutoInsights,
  type AutoInsightSeveridade,
} from '../../hooks/whatsapp/useWaAutoInsights';
import { parseUTC } from '../../services/whatsapp/waFormatters';

// Estilo por severidade dos insights automáticos (#64): info azul, warning
// âmbar, critical vermelho — alinhado à paleta Apple usada no módulo.
const SEVERIDADE_STYLE: Record<
  AutoInsightSeveridade,
  { icon: typeof Info; color: string; bg: string; border: string }
> = {
  info: { icon: Info, color: '#007AFF', bg: 'bg-blue-50', border: 'border-blue-100' },
  warning: {
    icon: AlertTriangle,
    color: '#FF9500',
    bg: 'bg-amber-50',
    border: 'border-amber-100',
  },
  critical: {
    icon: AlertOctagon,
    color: '#FF2D55',
    bg: 'bg-red-50',
    border: 'border-red-100',
  },
};

const tooltipStyle = {
  borderRadius: '12px',
  border: 'none',
  boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
  fontSize: '13px',
};
const SENT_COLORS = { positivo: '#34C759', neutro: '#8E8E93', negativo: '#FF2D55' };
const TOPIC_COLORS = ['#007AFF', '#5856D6', '#AF52DE', '#FF9500', '#FF2D55', '#34C759', '#8E8E93'];

export default function WaAiInsightsPage() {
  const navigate = useNavigate();
  const { data: allConversations } = useConversations();
  const { insights, isLoading, error, fetchInsights } = useWaDashboardAI();
  // Insights automáticos determinísticos (#64): vêm do backend (waApi), sem IA.
  // Estado próprio (loading/403/erro) — não interfere na análise de IA acima.
  const { data: autoInsights, isLoading: autoLoading, error: autoError } = useWaAutoInsights(30);

  const [period, setPeriod] = useState<'7d' | '30d' | 'mes' | 'custom'>('mes');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  // Feedback visivel quando a IA falha. 'sem_dados' nao e erro (estado normal
  // de poucos atendimentos), entao nao dispara toast.
  useEffect(() => {
    if (error && error !== 'sem_dados') {
      toast.error('Nao foi possivel gerar as recomendacoes da IA. Tente novamente.');
    }
  }, [error]);

  const dateRange = useMemo(() => {
    const now = new Date();
    if (period === '7d') return { start: subDays(now, 7), end: now };
    if (period === '30d') return { start: subDays(now, 30), end: now };
    if (period === 'mes') return { start: startOfMonth(now), end: now };
    if (period === 'custom' && customStart && customEnd)
      return { start: parseISO(customStart), end: parseISO(customEnd) };
    return null;
  }, [period, customStart, customEnd]);

  const conversations = useMemo(() => {
    // allConversations pode ser undefined enquanto a query carrega.
    const base = allConversations ?? [];
    if (!dateRange) return base;
    return base.filter(c => {
      // started_at vem em UTC do backend — parseUTC para comparar no instante
      // absoluto certo (evita off-by-3h em bordas de periodo).
      const d = c.started_at ? parseUTC(c.started_at) : null;
      return d ? isWithinInterval(d, { start: dateRange.start, end: dateRange.end }) : true;
    });
  }, [allConversations, dateRange]);

  // Insights locais (sem IA) das CONVERSAS DO PERIODO: sentimento pelas
  // avaliacoes reais + topicos pelos assuntos. Vem do hook compartilhado, o
  // mesmo usado no Dashboard — fonte unica, sem duplicar a logica.
  const localInsights = useWaLocalInsights(conversations);

  // IA sob demanda — usado apenas para recomendacoes (chamada leve)
  const handleAnalyze = () => {
    fetchInsights(
      {
        total_conversas: conversations.length,
        conversas_ativas: conversations.filter(c => c.status === 'open').length,
        em_espera: conversations.filter(c => c.status === 'waiting').length,
        encerradas: conversations.filter(c => c.status === 'closed').length,
        categorias: localInsights.topicos.map(t => `${t.nome}: ${t.quantidade}`).join(', '),
        tickets_finalizados: localInsights.totalFinalizados,
      },
      true,
    );
  };

  // Usa insights locais como base, IA complementa com recomendacoes
  const sentimentos = insights?.sentimentos || localInsights.sentimentos;
  const topicos = (insights?.topicos?.length ? insights.topicos : localInsights.topicos) || [];
  const recomendacoes = insights?.recomendacoes || [];

  const sentimentoData = [
    { name: 'Positivo', value: sentimentos.positivo, color: SENT_COLORS.positivo },
    { name: 'Neutro', value: sentimentos.neutro, color: SENT_COLORS.neutro },
    { name: 'Negativo', value: sentimentos.negativo, color: SENT_COLORS.negativo },
  ];

  const totalSentimento = sentimentos.positivo + sentimentos.neutro + sentimentos.negativo;

  // Conteúdo da seção de insights automáticos (#64). Extraído em função com
  // early-returns para tratar loading/403/erro/vazio sem ternários aninhados
  // (regra no-nested-ternary do Airbnb).
  const renderAutoInsights = () => {
    if (autoLoading) {
      return (
        <div className="flex items-center justify-center gap-2 py-12 text-gray-400">
          <Loader2 size={20} className="animate-spin" />
          <span className="text-sm">Calculando insights...</span>
        </div>
      );
    }
    if (autoError === 'sem_permissao') {
      // 403: somente admin/supervisor podem ver — mensagem clara, sem toast.
      return (
        <div className="flex flex-col items-center justify-center py-12 text-gray-400">
          <AlertTriangle size={40} className="mb-3 text-amber-300" />
          <p className="text-sm font-medium">Acesso restrito</p>
          <p className="mt-1 text-xs">
            Apenas administradores e supervisores podem ver os insights automáticos.
          </p>
        </div>
      );
    }
    if (autoError) {
      return (
        <div className="flex flex-col items-center justify-center py-12 text-gray-400">
          <AlertOctagon size={40} className="mb-3 text-red-200" />
          <p className="text-sm font-medium">Não foi possível carregar os insights</p>
          <p className="mt-1 text-xs">Tente novamente em instantes.</p>
        </div>
      );
    }
    if (autoInsights.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-12 text-gray-400">
          <Zap size={40} className="mb-3 text-blue-200" />
          <p className="text-sm font-medium">Nenhum insight disponível ainda</p>
          <p className="mt-1 text-xs">
            Assim que houver atendimentos suficientes, os insights aparecem aqui.
          </p>
        </div>
      );
    }
    return (
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {autoInsights.map(item => {
          const style = SEVERIDADE_STYLE[item.severidade] ?? SEVERIDADE_STYLE.info;
          const Icon = style.icon;
          return (
            <div key={item.key} className={`rounded-xl border ${style.border} ${style.bg} p-4`}>
              <div className="mb-2 flex items-center gap-2">
                <div className="rounded-lg bg-white/70 p-1.5">
                  <Icon size={16} style={{ color: style.color }} />
                </div>
                <span className="text-xs font-semibold text-gray-600">{item.titulo}</span>
              </div>
              <p className="mb-1 text-lg font-bold text-gray-900">{item.valor}</p>
              <p className="text-xs leading-relaxed text-gray-600">{item.detalhe}</p>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="min-h-screen space-y-6 bg-[#f5f5f7] p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div className="animate-fade-in flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate('/whatsapp/dashboard')}
            className="rounded-xl p-2 text-gray-400 transition-colors hover:bg-white hover:text-gray-600"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <div className="flex items-center gap-2">
              <div className="rounded-xl bg-gradient-to-br from-[#AF52DE] to-[#5856D6] p-2">
                <Brain size={20} className="text-white" />
              </div>
              <h1 className="text-gradient text-2xl font-bold sm:text-3xl">Analise de IA</h1>
            </div>
            <p className="ml-12 mt-1 text-sm text-gray-500">
              Sentimento baseado nas avaliacoes dos clientes; topicos pelos assuntos do periodo.
              Recomendacoes via IA sob demanda.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={handleAnalyze}
          disabled={isLoading}
          className="flex items-center gap-2 rounded-xl bg-gradient-to-r from-[#AF52DE] to-[#5856D6] px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:shadow-md disabled:opacity-60"
        >
          {isLoading ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
          {isLoading ? 'Gerando...' : 'Gerar Recomendacoes IA'}
        </button>
      </div>

      {/* Periodo */}
      <div className="animate-slide-up flex flex-wrap items-center gap-2">
        <div className="flex items-center overflow-hidden rounded-xl bg-white shadow-wa-apple">
          {(
            [
              ['7d', '7 dias'],
              ['30d', '30 dias'],
              ['mes', 'Este mes'],
              ['custom', 'Personalizado'],
            ] as const
          ).map(([key, label]) => (
            <button
              type="button"
              key={key}
              onClick={() => setPeriod(key)}
              className={`px-4 py-2.5 text-sm transition-colors ${period === key ? 'bg-[#AF52DE] font-semibold text-white' : 'text-gray-600 hover:bg-white/60'}`}
            >
              {label}
            </button>
          ))}
        </div>
        {period === 'custom' && (
          <div className="flex items-center gap-2 rounded-xl bg-white px-3 py-1.5 shadow-wa-apple">
            <Calendar size={14} className="text-gray-400" />
            <input
              type="date"
              value={customStart}
              onChange={e => setCustomStart(e.target.value)}
              className="w-[130px] bg-transparent text-sm text-gray-700 outline-none"
            />
            <span className="text-gray-300">—</span>
            <input
              type="date"
              value={customEnd}
              onChange={e => setCustomEnd(e.target.value)}
              className="w-[130px] bg-transparent text-sm text-gray-700 outline-none"
            />
            {customStart && customEnd && (
              <button
                type="button"
                onClick={() => {
                  setCustomStart('');
                  setCustomEnd('');
                }}
                className="p-1 text-gray-400 hover:text-gray-600"
              >
                <X size={14} />
              </button>
            )}
          </div>
        )}
        <span className="ml-2 text-xs text-gray-400">
          {conversations.length} conversas no periodo
        </span>
      </div>

      {/* Resumo KPIs */}
      <div className="animate-slide-up grid grid-cols-2 gap-3 md:grid-cols-4">
        {[
          {
            label: 'Sentimento Positivo',
            value: `${sentimentos.positivo}%`,
            icon: ThumbsUp,
            color: '#34C759',
            bg: 'bg-green-50',
          },
          {
            label: 'Sentimento Neutro',
            value: `${sentimentos.neutro}%`,
            icon: Minus,
            color: '#8E8E93',
            bg: 'bg-gray-50',
          },
          {
            label: 'Sentimento Negativo',
            value: `${sentimentos.negativo}%`,
            icon: ThumbsDown,
            color: '#FF2D55',
            bg: 'bg-red-50',
          },
          {
            label: 'Topicos Detectados',
            value: String(topicos.length),
            icon: Target,
            color: '#AF52DE',
            bg: 'bg-purple-50',
          },
        ].map(kpi => {
          const Icon = kpi.icon;
          return (
            <div key={kpi.label} className="rounded-2xl bg-white p-4 shadow-wa-apple">
              <div className={`rounded-xl p-2 ${kpi.bg} mb-2 w-fit`}>
                <Icon size={18} style={{ color: kpi.color }} />
              </div>
              <p className="text-2xl font-bold text-gray-900">{kpi.value}</p>
              <p className="text-xs text-gray-500">{kpi.label}</p>
            </div>
          );
        })}
      </div>

      {/* Charts Row */}
      <div className="animate-slide-up grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Sentimento Pie */}
        <div className="rounded-2xl bg-white p-5 shadow-wa-apple">
          <h3 className="mb-4 text-sm font-semibold text-gray-900">Analise de Sentimento</h3>
          {totalSentimento > 0 ? (
            <div className="flex items-center gap-6">
              <ResponsiveContainer width="50%" height={220}>
                <PieChart>
                  <Pie
                    data={sentimentoData}
                    cx="50%"
                    cy="50%"
                    innerRadius={45}
                    outerRadius={80}
                    paddingAngle={4}
                    dataKey="value"
                  >
                    {sentimentoData.map((entry, i) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => `${v}%`} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex-1 space-y-4">
                {sentimentoData.map(s => (
                  <div key={s.name}>
                    <div className="mb-1 flex items-center justify-between">
                      <span className="text-sm text-gray-600">{s.name}</span>
                      <span className="text-sm font-bold" style={{ color: s.color }}>
                        {s.value}%
                      </span>
                    </div>
                    <div className="h-2.5 overflow-hidden rounded-full bg-gray-100">
                      <div
                        className="h-full rounded-full transition-all duration-700"
                        style={{ width: `${s.value}%`, backgroundColor: s.color }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex h-[220px] flex-col items-center justify-center text-gray-400">
              <Sparkles size={32} className="mb-2 text-purple-300" />
              <p className="text-sm">Sem avaliacoes de clientes no periodo selecionado</p>
            </div>
          )}
        </div>

        {/* Topicos Bar */}
        <div className="rounded-2xl bg-white p-5 shadow-wa-apple">
          <h3 className="mb-4 text-sm font-semibold text-gray-900">Topicos Mais Frequentes</h3>
          {topicos.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={topicos.slice(0, 7)} layout="vertical" margin={{ left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e5ea" />
                <XAxis type="number" tick={{ fontSize: 12, fill: '#8E8E93' }} />
                <YAxis
                  dataKey="nome"
                  type="category"
                  tick={{ fontSize: 11, fill: '#8E8E93' }}
                  width={100}
                />
                <Tooltip contentStyle={tooltipStyle} />
                <Bar dataKey="quantidade" name="Mencoes" radius={[0, 6, 6, 0]}>
                  {topicos.slice(0, 7).map((_: any, i: number) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <Cell key={i} fill={TOPIC_COLORS[i % TOPIC_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex h-[220px] flex-col items-center justify-center text-gray-400">
              <Target size={32} className="mb-2 text-purple-300" />
              <p className="text-sm">Nenhuma conversa no periodo selecionado</p>
            </div>
          )}
        </div>
      </div>

      {/* Recomendacoes */}
      <div className="animate-slide-up rounded-2xl bg-white p-5 shadow-wa-apple sm:p-6">
        <div className="mb-5 flex items-center gap-3">
          <div className="rounded-xl bg-gradient-to-br from-[#FF9500] to-[#FF2D55] p-2.5">
            <Lightbulb size={20} className="text-white" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-gray-900">Recomendacoes da IA</h3>
            <p className="text-xs text-gray-500">
              Sugestoes de melhoria baseadas na analise dos atendimentos
            </p>
          </div>
        </div>

        {recomendacoes.length > 0 ? (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {recomendacoes.map((r: any, i: number) => {
              const titulo = typeof r === 'string' ? r : r.titulo || '';
              const descricao = typeof r === 'string' ? '' : r.descricao || '';
              const prioridade = typeof r === 'string' ? 'media' : r.prioridade || 'media';
              const prioridadeColors: Record<string, string> = {
                alta: 'bg-red-100 text-red-700',
                media: 'bg-orange-100 text-orange-700',
                baixa: 'bg-green-100 text-green-700',
              };

              return (
                <div
                  // eslint-disable-next-line react/no-array-index-key
                  key={i}
                  className="rounded-xl border border-purple-100/50 bg-gradient-to-br from-purple-50/50 to-orange-50/30 p-4"
                >
                  <div className="mb-2 flex items-start justify-between gap-2">
                    <span className="text-sm font-semibold text-gray-900">
                      {titulo || descricao}
                    </span>
                    <span
                      className={`flex-shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold ${prioridadeColors[prioridade] || prioridadeColors.media}`}
                    >
                      {prioridade}
                    </span>
                  </div>
                  {descricao && titulo && (
                    <p className="text-xs leading-relaxed text-gray-600">{descricao}</p>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 text-gray-400">
            <Lightbulb size={40} className="mb-3 text-orange-200" />
            <p className="text-sm font-medium">Nenhuma recomendacao gerada ainda</p>
            <p className="mt-1 text-xs">
              Selecione um periodo e clique em &quot;Gerar Recomendacoes IA&quot;
            </p>
          </div>
        )}
      </div>

      {/* Contexto dos dados */}
      <div className="animate-slide-up rounded-2xl bg-white p-5 shadow-wa-apple">
        <h3 className="mb-3 text-sm font-semibold text-gray-900">Contexto da Analise</h3>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          {[
            { label: 'Conversas analisadas', value: conversations.length },
            { label: 'Abertas', value: conversations.filter(c => c.status === 'open').length },
            { label: 'Em espera', value: conversations.filter(c => c.status === 'waiting').length },
            { label: 'Conversas avaliadas', value: localInsights.totalAvaliadas },
          ].map(item => (
            <div key={item.label} className="rounded-xl bg-gray-50 py-3 text-center">
              <p className="text-xl font-bold text-gray-900">{item.value}</p>
              <p className="text-xs text-gray-500">{item.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Insights automáticos (#64) — determinísticos, derivados das métricas
          reais (SLA, agentes, volume, clientes). Independentes da IA acima. */}
      <div className="animate-slide-up rounded-2xl bg-white p-5 shadow-wa-apple sm:p-6">
        <div className="mb-5 flex items-center gap-3">
          <div className="rounded-xl bg-gradient-to-br from-[#007AFF] to-[#5856D6] p-2.5">
            <Zap size={20} className="text-white" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-gray-900">Insights automáticos</h3>
            <p className="text-xs text-gray-500">
              Fatos calculados direto dos dados dos últimos 30 dias — sem IA
            </p>
          </div>
        </div>

        {renderAutoInsights()}
      </div>
    </div>
  );
}
